import {
  Users,
  Code,
  Shield,
  Zap,
  Heart,
  Mail,
  Github,
  Twitter,
  ExternalLink,
  Target,
  TrendingUp,
  Lightbulb,
  Award,
  Cpu,
  Palette,
  Coins,
  Percent
} from 'lucide-react'
import { cn } from '../lib/utils'
import { useJitoAPY } from '../hooks/useJitoAPY'

/**
 * Interface for team member information
 * Defines structure for team member cards
 */
interface TeamMember {
  name: string
  role: string
  description: string
  icon: React.ElementType
  color: 'cyan' | 'purple' | 'green' | 'magenta'
}

/**
 * Interface for technology stack items
 * Defines structure for tech showcase
 */
interface Technology {
  name: string
  category: string
  icon: React.ElementType
  description: string
  color: 'cyan' | 'purple' | 'green' | 'yellow'
}

/**
 * AboutUs Page Component
 * 
 * Showcases team and project information with:
 * - Team member profiles (Allen Kafchinski & Invent LLC)
 * - Project mission and core values
 * - V2 Liquid Staking innovation with Jito integration
 * - Technology stack showcase
 * - Contact information and social links
 * - Professional yet cyberpunk-themed design
 * - Responsive layout for all devices
 * - Full WCAG 2.1 AA accessibility compliance
 */
export function AboutUs(): JSX.Element {
  const { formattedApy } = useJitoAPY()

  /**
   * Team members configuration
   * Defines key team members and their roles
   */
  const teamMembers: TeamMember[] = [
    {
      name: 'Allen Kafchinski',
      role: 'Founder & Lead Developer',
      description: 'Blockchain enthusiast and full-stack developer passionate about decentralized governance and community empowerment through transparent technology.',
      icon: Code,
      color: 'cyan'
    },
    {
      name: 'Invent LLC',
      role: 'Development Studio',
      description: 'Innovation-focused development studio based in Las Vegas, NV specializing in blockchain solutions, smart contracts, and decentralized applications. We build Tools to help Projects Succeed.',
      icon: Lightbulb,
      color: 'purple'
    }
  ]

  /**
   * Core values configuration
   * Defines the project\'s guiding principles
   */
  const coreValues = [
    {
      icon: Shield,
      title: 'Transparency',
      description: 'All smart contracts are open source and verifiable on-chain. Every transaction is publicly auditable.',
      color: 'cyan'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Decisions are made democratically through share-weighted voting. The community always has the final say.',
      color: 'purple'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'Pioneering new approaches to decentralized governance with V2 liquid staking—earning yield while waiting for proposals.',
      color: 'green'
    },
    {
      icon: Heart,
      title: 'Empowerment',
      description: 'Giving communities the tools and resources to revive abandoned tokens and take control of their investments.',
      color: 'magenta'
    }
  ]

  /**
   * Technology stack configuration
   * Showcases the technologies powering the platform
   */
  const technologies: Technology[] = [
    {
      name: 'Solana',
      category: 'Blockchain',
      icon: Zap,
      description: 'High-performance blockchain for lightning-fast transactions',
      color: 'purple'
    },
    {
      name: 'Anchor',
      category: 'Framework',
      icon: Code,
      description: 'Solana development framework for secure smart contracts',
      color: 'cyan'
    },
    {
      name: 'Jito',
      category: 'Liquid Staking',
      icon: Coins,
      description: `Battle-tested liquid staking for ${formattedApy} APY yield generation`,
      color: 'green'
    },
    {
      name: 'React',
      category: 'Frontend',
      icon: Palette,
      description: 'Modern UI library with TypeScript for type safety',
      color: 'yellow'
    }
  ]

  /**
   * Get color classes for consistent theming
   * Returns Tailwind classes based on color name
   */
  const getColorClasses = (color: string) => {
    const colorMap = {
      cyan: {
        bg: 'bg-cyber-cyan/20',
        text: 'text-cyber-cyan',
        border: 'border-cyber-cyan/30'
      },
      purple: {
        bg: 'bg-cyber-purple/20',
        text: 'text-cyber-purple',
        border: 'border-cyber-purple/30'
      },
      green: {
        bg: 'bg-cyber-green/20',
        text: 'text-cyber-green',
        border: 'border-cyber-green/30'
      },
      magenta: {
        bg: 'bg-cyber-magenta/20',
        text: 'text-cyber-magenta',
        border: 'border-cyber-magenta/30'
      },
      yellow: {
        bg: 'bg-cyber-yellow/20',
        text: 'text-cyber-yellow',
        border: 'border-cyber-yellow/30'
      }
    }
    return colorMap[color as keyof typeof colorMap] || colorMap.cyan
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Page Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-cyber-cyan/20 rounded-cyber mb-6 animate-pulse-slow">
            <Users className="w-10 h-10 text-cyber-cyan" aria-hidden="true" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gradient-cyber mb-4">
            About CTO Pools
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Empowering communities to revive abandoned tokens through transparent, 
            decentralized governance on Solana—now with yield-earning liquid staking
          </p>
        </div>

        {/* Mission Statement */}
        <section className="mb-16" aria-labelledby="mission-heading">
          <div className="glass-card border border-cyber-cyan/30 p-8 md:p-12">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyber-cyan/20 rounded-cyber flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-cyber-cyan" aria-hidden="true" />
              </div>
              <div>
                <h2 id="mission-heading" className="text-3xl font-bold text-gradient-premium mb-4">Our Mission</h2>
                <p className="text-text-secondary text-lg leading-relaxed mb-4">
                  CTO Pools exists to give power back to cryptocurrency communities. Too many promising 
                  projects have been abandoned by their original developers, leaving token holders with 
                  worthless assets and no recourse. Community Takeovers have become a common occurrence on Solana, 
                  but there has been a distinctive lack of infrastructure to support these efforts. 
                  Our mission is to fill that gap.
                </p>
                <p className="text-text-secondary text-lg leading-relaxed">
                  We provide the infrastructure for communities to pool resources democratically, vote on 
                  funding proposals, and execute community takeovers—all through transparent and auditable 
                  smart contracts on the Solana blockchain.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* V2 Liquid Staking Innovation */}
        <section className="mb-16" aria-labelledby="v2-heading">
          <div className="glass-card border border-cyber-green/30 p-8 md:p-12">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyber-green/20 rounded-cyber flex items-center justify-center flex-shrink-0">
                <Coins className="w-6 h-6 text-cyber-green" aria-hidden="true" />
              </div>
              <div>
                <h2 id="v2-heading" className="text-3xl font-bold text-gradient-success mb-4">
                  V2: Jito Liquid Staking Integration
                </h2>
                <p className="text-text-secondary text-lg leading-relaxed mb-4">
                  CTO Pools V2 introduces a groundbreaking innovation: 
                  <span className="text-cyber-green font-semibold"> automatic liquid staking</span>. 
                  When community members donate SOL to a pool, it's instantly staked via the
                  <span className="text-cyber-cyan font-semibold"> Jito Stake Pool</span>—earning {formattedApy} APY
                  in staking rewards while waiting for governance decisions.
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="glass-dark rounded-cyber p-4">
                    <h4 className="text-cyber-green font-semibold mb-2 flex items-center">
                      <Percent className="w-5 h-5 mr-2" aria-hidden="true" />
                      Earn While Waiting
                    </h4>
                    <p className="text-text-secondary text-sm">
                      Pool funds receive jitoSOL—a liquid staking token that automatically accrues {formattedApy} APY.
                      Your community's treasury grows while waiting for the right proposal.
                    </p>
                  </div>
                  <div className="glass-dark rounded-cyber p-4">
                    <h4 className="text-cyber-cyan font-semibold mb-2 flex items-center">
                      <Shield className="w-5 h-5 mr-2" aria-hidden="true" />
                      Battle-Tested Security
                    </h4>
                    <p className="text-text-secondary text-sm">
                      Jito Stake Pool is one of Solana's most trusted liquid staking protocols with 
                      millions of SOL staked and a proven track record of security.
                    </p>
                  </div>
                  <div className="glass-dark rounded-cyber p-4">
                    <h4 className="text-cyber-purple font-semibold mb-2 flex items-center">
                      <Zap className="w-5 h-5 mr-2" aria-hidden="true" />
                      Instant Liquidity
                    </h4>
                    <p className="text-text-secondary text-sm">
                      jitoSOL can be unstaked at any time through governance. No lockup risk—communities 
                      maintain full control over their funds.
                    </p>
                  </div>
                  <div className="glass-dark rounded-cyber p-4">
                    <h4 className="text-cyber-magenta font-semibold mb-2 flex items-center">
                      <Heart className="w-5 h-5 mr-2" aria-hidden="true" />
                      Recovery Protection
                    </h4>
                    <p className="text-text-secondary text-sm">
                      V2 includes recovery proposals, allowing governance to recover mistakenly sent 
                      tokens or handle edge cases—protecting community funds.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="mb-16" aria-labelledby="values-heading">
          <h2 id="values-heading" className="text-3xl font-bold text-text-primary mb-8 text-center">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {coreValues.map((value, index) => {
              const IconComponent = value.icon
              const colors = getColorClasses(value.color)
              
              return (
                <div 
                  key={value.title}
                  className={cn(
                    "glass-card border p-6 transition-all duration-300 hover:scale-105",
                    colors.border
                  )}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start space-x-4">
                    <div className={cn(
                      "w-12 h-12 rounded-cyber flex items-center justify-center flex-shrink-0",
                      colors.bg
                    )}>
                      <IconComponent className={cn("w-6 h-6", colors.text)} aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <h3 className={cn("text-xl font-bold mb-2", colors.text)}>
                        {value.title}
                      </h3>
                      <p className="text-text-secondary leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Team Section */}
        <section className="mb-16" aria-labelledby="team-heading">
          <h2 id="team-heading" className="text-3xl font-bold text-text-primary mb-8 text-center">
            Meet the Team
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {teamMembers.map((member, index) => {
              const IconComponent = member.icon
              const colors = getColorClasses(member.color)
              
              return (
                <div 
                  key={member.name}
                  className={cn(
                    "glass-card border p-8 transition-all duration-300 hover:scale-105",
                    colors.border
                  )}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="text-center mb-6">
                    <div className={cn(
                      "w-20 h-20 rounded-cyber mx-auto mb-4 flex items-center justify-center",
                      colors.bg
                    )}>
                      <IconComponent className={cn("w-10 h-10", colors.text)} aria-hidden="true" />
                    </div>
                    <h3 className="text-2xl font-bold text-text-primary mb-2">
                      {member.name}
                    </h3>
                    <p className={cn("font-semibold mb-4", colors.text)}>
                      {member.role}
                    </p>
                  </div>
                  <p className="text-text-secondary leading-relaxed text-center">
                    {member.description}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        {/* Technology Stack */}
        <section className="mb-16" aria-labelledby="tech-heading">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-cyber-purple/20 rounded-cyber mb-4">
              <Cpu className="w-8 h-8 text-cyber-purple" aria-hidden="true" />
            </div>
            <h2 id="tech-heading" className="text-3xl font-bold text-text-primary mb-2">
              Technology Stack
            </h2>
            <p className="text-text-secondary">
              Built with cutting-edge blockchain and web technologies
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => {
              const IconComponent = tech.icon
              const colors = getColorClasses(tech.color)
              
              return (
                <div 
                  key={tech.name}
                  className={cn(
                    "glass-card border p-6 text-center transition-all duration-300 hover:scale-105",
                    colors.border
                  )}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={cn(
                    "w-16 h-16 rounded-cyber mx-auto mb-4 flex items-center justify-center",
                    colors.bg
                  )}>
                    <IconComponent className={cn("w-8 h-8", colors.text)} aria-hidden="true" />
                  </div>
                  <h3 className={cn("text-lg font-bold mb-1", colors.text)}>
                    {tech.name}
                  </h3>
                  <p className="text-sm text-text-muted mb-3">{tech.category}</p>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {tech.description}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        {/* Why Solana */}
        <section className="mb-16" aria-labelledby="solana-heading">
          <div className="glass-card border border-cyber-purple/30 p-8 md:p-12">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-12 h-12 bg-cyber-purple/20 rounded-cyber flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-cyber-purple" aria-hidden="true" />
              </div>
              <div>
                <h2 id="solana-heading" className="text-3xl font-bold text-gradient-premium mb-4">
                  Why Solana?
                </h2>
                <div className="space-y-4 text-text-secondary text-lg leading-relaxed">
                  <p>
                    We chose Solana as our blockchain platform for several critical reasons:
                  </p>
                  <ul className="space-y-3 ml-6">
                    <li className="flex items-start space-x-3">
                      <Award className="w-5 h-5 text-cyber-cyan flex-shrink-0 mt-1" aria-hidden="true" />
                      <span>
                        <strong className="text-cyber-cyan">High Performance:</strong> 65,000+ transactions per second 
                        enable instant voting and governance actions
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Award className="w-5 h-5 text-cyber-green flex-shrink-0 mt-1" aria-hidden="true" />
                      <span>
                        <strong className="text-cyber-green">Low Costs:</strong> Transaction fees under $0.01 make 
                        community participation accessible to everyone
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Award className="w-5 h-5 text-cyber-purple flex-shrink-0 mt-1" aria-hidden="true" />
                      <span>
                        <strong className="text-cyber-purple">Developer Friendly:</strong> Anchor framework provides 
                        robust security and rapid development
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Award className="w-5 h-5 text-cyber-magenta flex-shrink-0 mt-1" aria-hidden="true" />
                      <span>
                        <strong className="text-cyber-magenta">Growing Ecosystem:</strong> Vibrant community and 
                        extensive DeFi infrastructure for token operations
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Award className="w-5 h-5 text-cyber-yellow flex-shrink-0 mt-1" aria-hidden="true" />
                      <span>
                        <strong className="text-cyber-yellow">Liquid Staking:</strong> Access to Jito and other 
                        liquid staking protocols for automatic yield generation
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section aria-labelledby="contact-heading">
          <div className="glass-card border border-cyber-cyan/30 p-8 md:p-12 text-center">
            <h2 id="contact-heading" className="text-3xl font-bold text-gradient-cyber mb-4">
              Get In Touch
            </h2>
            <p className="text-text-secondary text-lg mb-8 max-w-2xl mx-auto">
              Have questions, suggestions, or want to contribute? We'd love to hear from you!
            </p>

            {/* Contact Methods */}
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              
              {/* Email */}
              <a
                href="mailto:inventcryptomain@gmail.com"
                className="glass-card border border-cyber-cyan/20 p-6 hover:border-cyber-cyan/50 hover:scale-105 transition-all duration-300 group"
                aria-label="Email us at inventcryptomain@gmail.com"
              >
                <Mail className="w-8 h-8 text-cyber-cyan mx-auto mb-3 group-hover:scale-110 transition-transform" aria-hidden="true" />
                <h3 className="text-cyber-cyan font-semibold mb-1">Email</h3>
                <p className="text-sm text-text-muted">inventcryptomain@gmail.com</p>
              </a>

              {/* GitHub */}
              <a
                href="https://github.com/InventOfficial/CTO-Pools"
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card border border-cyber-purple/20 p-6 hover:border-cyber-purple/50 hover:scale-105 transition-all duration-300 group"
                aria-label="Visit our GitHub repository"
              >
                <Github className="w-8 h-8 text-cyber-purple mx-auto mb-3 group-hover:scale-110 transition-transform" aria-hidden="true" />
                <h3 className="text-cyber-purple font-semibold mb-1">GitHub</h3>
                <p className="text-sm text-text-muted flex items-center justify-center">
                  Open Source
                  <ExternalLink className="w-3 h-3 ml-1" aria-hidden="true" />
                </p>
              </a>

              {/* Twitter */}
              <a
                href="https://x.com/CtoPools"
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card border border-cyber-green/20 p-6 hover:border-cyber-green/50 hover:scale-105 transition-all duration-300 group"
                aria-label="Follow us on X (Twitter)"
              >
                <Twitter className="w-8 h-8 text-cyber-green mx-auto mb-3 group-hover:scale-110 transition-transform" aria-hidden="true" />
                <h3 className="text-cyber-green font-semibold mb-1">Twitter</h3>
                <p className="text-sm text-text-muted flex items-center justify-center">
                  @CtoPools
                  <ExternalLink className="w-3 h-3 ml-1" aria-hidden="true" />
                </p>
              </a>
            </div>

            {/* Additional Info */}
            <div className="glass-dark rounded-cyber p-6 max-w-2xl mx-auto">
              <p className="text-text-secondary leading-relaxed">
                CTO Pools is an open-source project. We welcome contributions from developers, 
                designers, and community members. Check out our GitHub repository to get started!
              </p>
            </div>
          </div>
        </section>

        {/* Footer Tagline */}
        <div className="mt-12 text-center">
          <p className="text-text-muted italic">
            "Building the future of decentralized governance, one pool at a time—with yield."
          </p>
        </div>
      </div>
    </div>
  )
}

export default AboutUs
